import { BuildingState, Building, ServiceBuilding } from 'store/building/types';
import { RootState } from '../reducer';
import { createSelector } from 'reselect';

export const buildingStateSelector = (state: RootState): BuildingState => state.building;

// User building selectors (user's home building - for theme, analytics)
export const selectUserBuilding = createSelector(buildingStateSelector, buildingState => buildingState.building);

// Alias for backwards compatibility
export const selectBuilding = selectUserBuilding;

export const selectBuildingStatus = createSelector(
  buildingStateSelector,
  buildingState => buildingState.getBuilding.status,
);

// Service building selectors (building where the service is - from building_context)
export const selectServiceBuilding = createSelector(
  buildingStateSelector,
  buildingState => buildingState.serviceBuilding,
);

/**
 * Returns the timezone for displaying service times.
 * Uses service building (from building_context) for cross-building bookings,
 * falls back to user building for same-building bookings.
 */
export const selectBuildingTimeZone = createSelector(
  selectServiceBuilding,
  selectUserBuilding,
  (serviceBuilding: ServiceBuilding | null, userBuilding: Building | null): string | null =>
    serviceBuilding?.timezone ?? userBuilding?.timezone ?? null,
);

/**
 * Returns the locale for formatting service prices/dates.
 * Uses service building (from building_context) for cross-building bookings,
 * falls back to user building for same-building bookings.
 */
export const selectBuildingLocale = createSelector(
  selectServiceBuilding,
  selectUserBuilding,
  (serviceBuilding: ServiceBuilding | null, userBuilding: Building | null): string | null =>
    serviceBuilding?.locale ?? userBuilding?.locale ?? null,
);

/**
 * Returns the currency for the service building.
 * Uses service building (from building_context) for cross-building bookings.
 */
export const selectBuildingCurrency = createSelector(
  selectServiceBuilding,
  (serviceBuilding: ServiceBuilding | null): string | null => serviceBuilding?.currency_type ?? null,
);

// Explicit service building selectors (when you specifically need service building data)
export const selectServiceBuildingTimeZone = createSelector(
  selectServiceBuilding,
  (serviceBuilding: ServiceBuilding | null): string | null => serviceBuilding?.timezone ?? null,
);

export const selectServiceBuildingLocale = createSelector(
  selectServiceBuilding,
  (serviceBuilding: ServiceBuilding | null): string | null => serviceBuilding?.locale ?? null,
);

export const selectServiceBuildingCurrency = createSelector(
  selectServiceBuilding,
  (serviceBuilding: ServiceBuilding | null): string | null => serviceBuilding?.currency_type ?? null,
);

// Explicit user building selectors (when you specifically need user's home building data)
export const selectUserBuildingTimeZone = createSelector(
  selectUserBuilding,
  (building: Building | null): string | null => building?.timezone ?? null,
);

export const selectUserBuildingLocale = createSelector(
  selectUserBuilding,
  (building: Building | null): string | null => building?.locale ?? null,
);
